export const MAX_COMBINATORICS_INPUT = 10_000n
export const MAX_COMBINATORICS_INPUT_LENGTH = 5
export const MAX_COMBINATORICS_RESULT_DIGITS = 10_000

export type CombinatoricsMode =
  | 'ordered-without-repetition'
  | 'ordered-with-repetition'
  | 'unordered-without-repetition'
  | 'unordered-with-repetition'

export type CombinatoricsError =
  | 'empty_input'
  | 'invalid_integer'
  | 'input_limit'
  | 'selection_exceeds_population'
  | 'size_limit'
  | 'processing_failed'

export interface CombinatoricsResult {
  mode: CombinatoricsMode
  name: string
  formula: string
  substitution: string
  value: string
}

export type CombinatoricsCalculation =
  | { status: 'success'; result: CombinatoricsResult }
  | { status: 'error'; error: CombinatoricsError }

type ParsedInput =
  | { status: 'ready'; value: bigint }
  | { status: 'error'; error: Extract<CombinatoricsError, 'empty_input' | 'invalid_integer' | 'input_limit'> }

type DivisionObserver = (step: {
  numerator: bigint
  divisor: bigint
  remainder: bigint
}) => void

const MODE_COPY: Record<CombinatoricsMode, { name: string; formula: string }> = {
  'ordered-without-repetition': {
    name: 'Размещения без повторений',
    formula: 'A(n, k) = n! / (n − k)!',
  },
  'ordered-with-repetition': {
    name: 'Размещения с повторениями',
    formula: 'nᵏ',
  },
  'unordered-without-repetition': {
    name: 'Сочетания без повторений',
    formula: 'C(n, k) = n! / (k! × (n − k)!)',
  },
  'unordered-with-repetition': {
    name: 'Сочетания с повторениями',
    formula: 'C(n + k − 1, k)',
  },
}

export function parseCombinatoricsInput(input: string): ParsedInput {
  const trimmed = input.trim()
  if (trimmed === '') return { status: 'error', error: 'empty_input' }
  if (trimmed.length > MAX_COMBINATORICS_INPUT_LENGTH) {
    return { status: 'error', error: 'input_limit' }
  }
  if (!/^[0-9]+$/.test(trimmed)) {
    return { status: 'error', error: 'invalid_integer' }
  }

  const value = BigInt(trimmed)
  if (value > MAX_COMBINATORICS_INPUT) {
    return { status: 'error', error: 'input_limit' }
  }

  return { status: 'ready', value }
}

export function calculateCombinatorics(
  mode: CombinatoricsMode,
  nInput: string,
  kInput: string,
): CombinatoricsCalculation {
  const parsedN = parseCombinatoricsInput(nInput)
  if (parsedN.status === 'error') return parsedN
  const parsedK = parseCombinatoricsInput(kInput)
  if (parsedK.status === 'error') return parsedK

  const n = parsedN.value
  const k = parsedK.value
  if (!allowsRepetition(mode) && k > n) {
    return { status: 'error', error: 'selection_exceeds_population' }
  }
  if (preflightLog10(mode, n, k) >= MAX_COMBINATORICS_RESULT_DIGITS + 0.01) {
    return { status: 'error', error: 'size_limit' }
  }

  try {
    const exactValue = calculateExact(mode, n, k)
    const value = exactValue.toString()
    if (value.length > MAX_COMBINATORICS_RESULT_DIGITS) {
      return { status: 'error', error: 'size_limit' }
    }

    return {
      status: 'success',
      result: {
        mode,
        ...MODE_COPY[mode],
        substitution: buildSubstitution(mode, n, k, value),
        value,
      },
    }
  } catch {
    return { status: 'error', error: 'processing_failed' }
  }
}

export function binomialChecked(
  total: bigint,
  selection: bigint,
  observeDivision?: DivisionObserver,
): bigint {
  if (selection < 0n || total < 0n || selection > total) {
    throw new RangeError('Invalid binomial arguments')
  }

  const r = selection < total - selection ? selection : total - selection
  let result = 1n
  for (let index = 1n; index <= r; index += 1n) {
    const numerator = result * (total - r + index)
    const remainder = numerator % index
    observeDivision?.({ numerator, divisor: index, remainder })
    if (remainder !== 0n) {
      throw new Error('Non-exact binomial division')
    }
    result = numerator / index
  }
  return result
}

function allowsRepetition(mode: CombinatoricsMode): boolean {
  return mode === 'ordered-with-repetition' || mode === 'unordered-with-repetition'
}

function calculateExact(mode: CombinatoricsMode, n: bigint, k: bigint): bigint {
  if (mode === 'ordered-without-repetition') return fallingProduct(n, k)
  if (mode === 'ordered-with-repetition') return integerPower(n, k)
  if (mode === 'unordered-without-repetition') return binomialChecked(n, k)
  if (n === 0n) return k === 0n ? 1n : 0n
  return binomialChecked(n + k - 1n, k)
}

function fallingProduct(n: bigint, k: bigint): bigint {
  let result = 1n
  for (let offset = 0n; offset < k; offset += 1n) {
    result *= n - offset
  }
  return result
}

function integerPower(base: bigint, exponent: bigint): bigint {
  let result = 1n
  let factor = base
  let remaining = exponent
  while (remaining > 0n) {
    if (remaining % 2n === 1n) result *= factor
    remaining /= 2n
    if (remaining > 0n) factor *= factor
  }
  return result
}

function preflightLog10(mode: CombinatoricsMode, n: bigint, k: bigint): number {
  if (k === 0n) return 0
  if (n === 0n && allowsRepetition(mode)) return 0

  let log10 = 0
  if (mode === 'ordered-without-repetition') {
    for (let offset = 0n; offset < k; offset += 1n) {
      log10 += Math.log10(Number(n - offset))
    }
  } else if (mode === 'ordered-with-repetition') {
    log10 = Number(k) * Math.log10(Number(n))
  } else {
    const total = mode === 'unordered-without-repetition' ? n : n + k - 1n
    const r = k < total - k ? k : total - k
    for (let index = 1n; index <= r; index += 1n) {
      log10 += Math.log10(Number(total - r + index)) - Math.log10(Number(index))
    }
  }

  if (!Number.isFinite(log10)) return MAX_COMBINATORICS_RESULT_DIGITS + 1
  return log10
}

function buildSubstitution(
  mode: CombinatoricsMode,
  n: bigint,
  k: bigint,
  value: string,
): string {
  if (mode === 'ordered-without-repetition') {
    return `A(${n}, ${k}) = ${n}! / (${n} − ${k})! = ${fallingFactors(n, k)} = ${value}`
  }
  if (mode === 'ordered-with-repetition') {
    return `${n}^${k} = ${value}`
  }
  if (mode === 'unordered-without-repetition') {
    return `C(${n}, ${k}) = ${n}! / (${k}! × (${n} − ${k})!) = ${value}`
  }

  if (n === 0n && k > 0n) return `n = 0, k = ${k}: выбрать непустой набор невозможно = 0`
  if (n === 0n && k === 0n) return 'n = 0, k = 0: пустая выборка = 1'
  const total = n + k - 1n
  return `C(${n} + ${k} − 1, ${k}) = C(${total}, ${k}) = ${value}`
}

function fallingFactors(n: bigint, k: bigint): string {
  if (k === 0n) return '1'
  if (k <= 8n) {
    const factors: string[] = []
    for (let offset = 0n; offset < k; offset += 1n) {
      factors.push((n - offset).toString())
    }
    return factors.join(' × ')
  }
  return `${n} × ${n - 1n} × … × ${n - k + 1n}`
}
